import { useState, useContext } from "react";
import "./Navbar.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeContext } from "../context/themeContext";
import { FaMoon, FaSun } from "react-icons/fa";
import { FaUserCircle } from "react-icons/fa";
import { AvatarContext } from "../context/AvatarContext";
import { UserContext } from "../context/UserContext";
import { navbarTransition } from "../animations/useNavbarScroll";

const Navbar = ({ openLogin, onMenuToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileExpanded, setProfileExpanded] = useState(false);
  const { activeTheme, handleThemeChange } = useContext(ThemeContext);
  const { avatar, avatarColor } = useContext(AvatarContext);
  const { user } = useContext(UserContext);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const closeMenu = () => {
    setIsOpen(false);
    if (onMenuToggle) onMenuToggle(false);
    const menu = document.getElementById("navbarSupportedContent");
    if (menu && menu.classList.contains("show")) {
      menu.classList.remove("show");
    }
  };

  const toggleMenu = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (onMenuToggle) onMenuToggle(newState);
  };

  return (
    <>
    <motion.nav
      className="header_section"
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={navbarTransition}
      style={{
        /* Locked full-width bar at the very top */
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        height: '68px',
        display: 'flex',
        alignItems: 'center',
        zIndex: 9999,
        borderRadius: 0,
        padding: '0 32px',
        /* Glassmorphism background — dark / light themed */
        background: activeTheme === 'dark'
          ? 'rgba(12, 12, 12, 0.92)'
          : 'rgba(253, 246, 236, 0.92)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        borderBottom: activeTheme === 'dark'
          ? '1px solid rgba(255,255,255,0.07)'
          : '1px solid rgba(0,0,0,0.08)',
        boxShadow: activeTheme === 'dark'
          ? '0 2px 24px rgba(0,0,0,0.6)'
          : '0 2px 16px rgba(0,0,0,0.10)',
        transition: 'background 0.35s ease, box-shadow 0.35s ease',
        fontFamily: "'Space Grotesk', 'Barlow', sans-serif",
      }}
    >
      <div className="container-fluid" style={{ height: '100%' }}>
        <nav className="navbar navbar-expand-lg custom_nav-container" style={{ height: '100%' }}>
          {/* LOGO */}
          <a className="navbar-brand" href="/">
            <span>MarsMate <span className="brand-ai-text">AI</span></span>
          </a>

          <div className="mobile-actions">
            {/* MOBILE THEME TOGGLE */}
            <div className="theme-switch navbar-mobile-only">
              <input
                type="checkbox"
                id="mobile-theme-toggle"
                checked={activeTheme === "dark"}
                onChange={() =>
                  handleThemeChange(activeTheme === "dark" ? "light" : "dark")
                }
              />
              <label htmlFor="mobile-theme-toggle" />
            </div>

            {/* PROFILE ICON / AVATAR */}
            {user ? (
              <div
                className="nav-profile-avatar"
                style={{ background: avatar ? "transparent" : (avatarColor || "#C1440E") }}
                onClick={() => { closeMenu(); setProfileExpanded(true); }}
                role="button"
                aria-label="View profile"
              >
                {avatar
                  ? <img src={avatar} alt="Profile" className="nav-profile-img" />
                  : (user?.name?.[0]?.toUpperCase() || "U")
                }
              </div>
            ) : (
              <button
                className="login-mob"
                onClick={() => {
                  if (typeof openLogin === "function") openLogin();
                  closeMenu();
                }}
                aria-label="Login"
              >
                <FaUserCircle className="login-mob-icon" />
              </button>
            )}

            {/* HAMBURGER */}
            <button
              className={`navbar-toggler ${isOpen ? "open" : ""}`}
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarSupportedContent"
              onClick={toggleMenu}
            >
              <span className="toggler-icon"></span>
              <span className="toggler-icon"></span>
              <span className="toggler-icon"></span>
            </button>
          </div>

          {/* MENU */}
          <div className="collapse navbar-collapse" id="navbarSupportedContent">
            <ul className="navbar-nav ms-auto">
              <li className="nav-item theme-toggle-item"></li>

              <li className="nav-item">
                <button className="nav-link" onClick={() => { scrollToSection("home"); closeMenu(); }}>
                  Home
                </button>
              </li>
              <li className="nav-item">
                <button className="nav-link" onClick={() => { scrollToSection("about"); closeMenu(); }}>
                  About
                </button>
              </li>
              <li className="nav-item">
                <button className="nav-link" onClick={() => { scrollToSection("features"); closeMenu(); }}>
                  Features
                </button>
              </li>
              <li className="nav-item">
                <button className="nav-link" onClick={() => { scrollToSection("services"); closeMenu(); }}>
                  Services
                </button>
              </li>
              <li className="nav-item">
                <button className="nav-link" onClick={() => { scrollToSection("why"); closeMenu(); }}>
                  Why Us
                </button>
              </li>
              <li className="nav-item">
                <button className="nav-link" onClick={() => { scrollToSection("getintouch"); closeMenu(); }}>
                 Requirements
                </button>
              </li>
              <li className="nav-item">
                <button className="nav-link" onClick={() => { scrollToSection("footer"); closeMenu(); }}>
                  Contact Us
                </button>
              </li>
              {user ? (
                <li className="nav-item d-flex align-items-center ms-lg-2">
                  <div
                    className="nav-profile-avatar nav-profile-avatar--desktop"
                    style={{ background: avatar ? "transparent" : (avatarColor || "#C1440E") }}
                    onClick={() => setProfileExpanded(true)}
                    role="button"
                    aria-label="View profile"
                  >
                    {avatar
                      ? <img src={avatar} alt="Profile" className="nav-profile-img" />
                      : (user?.name?.[0]?.toUpperCase() || "U")
                    }
                  </div>
                </li>
              ) : (
                <li className="nav-item">
                  <button
                    className="nav-link d-mob-none"
                    onClick={() => {
                      if (typeof openLogin === "function") openLogin();
                      closeMenu();
                    }}
                  >
                    Login
                  </button>
                </li>
              )}

              <li className="nav-item d-flex align-items-center ms-lg-3">
                <button
                  className={`theme-toggle ${activeTheme === "dark" ? "on" : "off"}`}
                  onClick={() => handleThemeChange(activeTheme === "dark" ? "light" : "dark")}
                >
                  <span className="toggle-icon">
                    {activeTheme === "dark" ? <FaMoon /> : <FaSun />}
                  </span>
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </div>
    </motion.nav>

    {/* ── Expanded profile overlay ── */}
    <AnimatePresence>
      {profileExpanded && (
        <motion.div
          className="profile-expanded-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={() => setProfileExpanded(false)}
        >
          <motion.div
            className="profile-expanded-content"
            initial={{ scale: 0.55, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.55, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
            onClick={e => e.stopPropagation()}
          >
            <button
              className="profile-expanded-close"
              onClick={() => setProfileExpanded(false)}
              aria-label="Close"
            >
              ×
            </button>

            {/* Spinning gradient ring */}
            <div className="profile-expanded-ring">
              <div
                className="profile-expanded-circle"
                style={{ background: avatar ? "transparent" : (avatarColor || "#C1440E") }}
              >
                {avatar
                  ? <img src={avatar} alt="Profile" className="profile-expanded-img" />
                  : <span className="profile-expanded-initial">{user?.name?.[0]?.toUpperCase() || "U"}</span>
                }
              </div>
            </div>

            <p className="profile-expanded-name">{user?.name || "User"}</p>
            {user?.email && <p className="profile-expanded-email">{user.email}</p>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export default Navbar;